import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
    AlertService,
    ConfirmationPopupService,
    ModalDialogService,
    MultiSubscriptionComponent,
    WidgetDeleteAlertComponent,
} from '@ddv/common-components';
import { Entitlement, FeatureFlagService, UserEntitlements } from '@ddv/entitlements';
import {
    CONFIRM_DELETE,
    DdvDate,
    getGlobalSaveDialogOptions,
    GroupedWidgets,
    groupWidgetsByCriteria,
    NamedQuery,
    VisualizationType,
    WidgetSnapshot,
} from '@ddv/models';
import { NamedQueriesService } from '@ddv/named-queries';
import { visualizationDescriptorMap } from '@ddv/visualizations';
import { catchError, combineLatest, map, Observable, of, switchMap, tap } from 'rxjs';

import { LegacyWidgetsService } from '../../services/legacy-widgets.service';
import { WidgetsService } from '../../services/widgets.service';
import { WidgetConfigurationManager } from '../../widget.configuration';

export enum Tabs {
    ALL = 'ALL',
    GLOBAL = 'GLOBAL',
    PRIVATE = 'PRIVATE'
}

export enum Groups {
    QUERY = 'Query',
    QUERY_TYPE = 'Query Type',
    GLOBAL = 'Global',
    NO_GROUP = 'No Filter',
    VIZ_TYPE = 'Visualization Type',
    WHATS_NEW = "What's New"
}

@Component({
    selector: 'app-widget-finder',
    templateUrl: './widget-finder.component.html',
    styleUrls: ['./widget-finder.component.scss'],
    standalone: false,
})
export class WidgetFinderComponent extends MultiSubscriptionComponent implements OnInit {
    @Input() showOnlyGlobal = false;
    @Input() entitlements: UserEntitlements | undefined;
    @Input() clientCode = '';

    @Output() widgetAdded = new EventEmitter<WidgetSnapshot>();
    @ViewChild('searchBox', { static: true }) searchBox: ElementRef | undefined;

    private readonly alertService = inject(AlertService);
    private readonly confirmationService = inject(ConfirmationPopupService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly legacyWidgetsService = inject(LegacyWidgetsService);
    private readonly modalService = inject(ModalDialogService);
    private readonly namedQueryService = inject(NamedQueriesService);
    private readonly widgetsService = inject(WidgetsService);

    groupedWidgets: GroupedWidgets[] = [];
    selectedTab: string | undefined;
    showWidgetDescById = 0;
    toggleWidgetData: boolean[] = [];
    showGroupByDropdown = false;
    hasViewAndEditAll = false; // This will no longer be needed after the widget migration
    protected loaderInWidgetFinder = true;
    protected searchParam = '';
    protected groupText: string | undefined;
    protected groupsList = Groups;
    protected tabs = Tabs;
    protected showPrivateTab = false;
    protected showDeleteWidgetButton = false;
    private widgetsCollection: WidgetSnapshot[] = [];
    private namedQueriesCollection: NamedQuery[] = [];
    private readonly groupByWhatsNewText = Groups.WHATS_NEW;

    ngOnInit(): void {
        this.selectedTab = this.showOnlyGlobal ? Tabs.GLOBAL : Tabs.ALL;
        this.groupText = Groups.QUERY;

        this.fetchWidgets().subscribe({
            next: () => {
                this.loaderInWidgetFinder = false;
            },
        });

        this.hasViewAndEditAll = !!this.entitlements?.hasPermission(Entitlement.VIEW_AND_EDIT_ALL);
        this.showDeleteWidgetButton = !!this.entitlements?.hasPermission(Entitlement.WIDGET_DELETE);
    }

    toggleWidgetDescription(widgetId: number): void {
        this.showWidgetDescById = (this.showWidgetDescById !== widgetId) ? widgetId : -1;
    }

    protected addWidget(widget: WidgetSnapshot): void {
        if (this.showOnlyGlobal) {
            const confirmDialogOptions = getGlobalSaveDialogOptions();
            this.confirmationService.showConfirmationPopup(confirmDialogOptions).subscribe({
                next: (action) => {
                    if (action === 'confirm') {
                        this.widgetAdded.emit(widget);
                        this.loaderInWidgetFinder = true;
                    }
                },
            });
        } else {
            this.widgetAdded.emit(widget);
            this.loaderInWidgetFinder = true;
        }
    }

    protected updateSelectedGroup(group: string): void {
        if (this.groupText !== group) {
            this.groupText = group;

            this.showGroupByDropdown = false;
            this.filterSearchResults();
        }
    }

    protected toggleGroupByDropdown(): void {
        this.showGroupByDropdown = !this.showGroupByDropdown;
    }

    protected onTabChange(selectedTabName: string): void {
        this.selectedTab = selectedTabName;
        this.filterSearchResults();
    }

    protected toggleWidgetsData(index: number): void {
        this.toggleWidgetData[index] = !this.toggleWidgetData[index];
    }

    protected filterSearchResults(): void {
        this.addNamedQueryAttributesToWidgets();
        this.formatResults(this.widgetsCollection);

        if (this.searchParam?.trim()) {
            const normalizedSearchTerm = this.searchParam.toLowerCase();
            this.groupedWidgets = this.groupedWidgets.filter((widgetGroup) => {
                widgetGroup.widgets = widgetGroup.widgets.filter((widget) => {
                    return widget.name.toLowerCase().includes(normalizedSearchTerm);
                });

                if (widgetGroup.widgets.length) {
                    return widgetGroup;
                }

                return;
            });
        }
    }

    protected deleteWidget(widgetId: number, groupedWidgetsIndex: number, widgetIndexInGroup: number): void {
        const confirmDialogOptions = {
            message: CONFIRM_DELETE,
            confirmButtonText: 'Delete',
            denyButtonText: 'Cancel',
        };
        this.confirmationService.showConfirmationPopup(confirmDialogOptions).subscribe({
            next: (action) => {
                if (action === 'confirm') {
                    this.onWidgetDelete(widgetId, groupedWidgetsIndex, widgetIndexInGroup);
                }
            },
        });
    }

    private fetchWidgets(): Observable<void> {
        return this.featureFlagService.isFlagEnabled('use-aws-for-widgets', this.clientCode ?? '')
            .pipe(
                switchMap((isFlagEnabled) => {
                    if (isFlagEnabled) {
                        return combineLatest([
                            this.widgetsService.fetchAllWidgets(this.clientCode),
                            this.namedQueryService.fetchNamedQueries(),
                        ]);
                    }
                    return combineLatest([
                        this.legacyWidgetsService.fetchAllWidgets(),
                        this.namedQueryService.fetchNamedQueries(),
                    ]);
                }),
                map(([widgets, namedQueries]) => {
                    this.namedQueriesCollection = namedQueries;
                    this.widgetsCollection = widgets;
                    this.filterSearchResults();
                }),
                tap(() => {
                    this.determinePrivateTabVisibility();
                }),
                catchError((error: { statusText: string }) => {
                    this.alertService.error(error.statusText);
                    return of(undefined);
                }),
            );
    }

    private determinePrivateTabVisibility(): void {
        if (!this.showOnlyGlobal && this.filterPrivate(this.widgetsCollection).length) {
            this.showPrivateTab = true;
        } else {
            this.showPrivateTab = false;
        }
    }

    private getWidgetsByTab(widgets: WidgetSnapshot[]): WidgetSnapshot[] {
        switch (this.selectedTab) {
            case Tabs.ALL:
                return widgets;
            case Tabs.PRIVATE:
                return this.filterPrivate(widgets);
            case Tabs.GLOBAL:
                return this.filterGlobal(widgets);
        }

        return [];
    }

    private groupBy(data: WidgetSnapshot[]): GroupedWidgets[] {
        switch (this.groupText) {
            case Groups.QUERY:
                return this.groupByNamedQuery(data);
            case Groups.QUERY_TYPE:
                return this.groupByQueryType(data);
            case Groups.GLOBAL:
                return this.groupByQueryType(this.filterGlobal(data));
            case Groups.NO_GROUP:
                return [{ groupName: 'No Filter', widgets: data, totalWidgets: data.length }];
            case Groups.VIZ_TYPE:
                return this.groupByVisualizationType(data);
            case Groups.WHATS_NEW:
                return this.groupByDate(data);
        }

        return [];
    }

    private groupByNamedQuery(widgets: WidgetSnapshot[]): GroupedWidgets[] {
        const groupedByNamedQuery = groupWidgetsByCriteria(widgets, 'namedQueryName');
        return this.groupWidgets(groupedByNamedQuery);
    }

    private groupByQueryType(widgets: WidgetSnapshot[]): GroupedWidgets[] {
        const groupedByQueryType = groupWidgetsByCriteria(widgets, 'namedQueryType');
        return this.groupWidgets(groupedByQueryType);
    }

    private groupByDate(widgets: WidgetSnapshot[]): GroupedWidgets[] {
        const groupedByDate = groupWidgetsByCriteria(widgets, 'createdDate');
        return this.groupWidgets(groupedByDate);
    }

    private groupWidgets(widgets: { [key: string]: WidgetSnapshot[] }): GroupedWidgets[] {
        return Object.entries(widgets).map((group) => {
            return { groupName: group[0], widgets: group[1], totalWidgets: group[1].length };
        });
    }

    private groupByVisualizationType(widgets: WidgetSnapshot[]): GroupedWidgets[] {
        const groupedByCoreWidgetType = groupWidgetsByCriteria(widgets, 'coreWidgetType');
        const groupedWidgets: GroupedWidgets[] = [];
        Object.entries(groupedByCoreWidgetType).forEach((group) => {
            if (group[0] !== 'VISUALIZATION') {
                groupedWidgets.push({ groupName: group[0], widgets: group[1], totalWidgets: group[1].length });
            } else {
                const visualizationWidgets = groupWidgetsByCriteria(group[1], 'visualizations');
                Object.entries(visualizationWidgets).forEach((vizGroup) => {
                    groupedWidgets.push({
                        groupName: vizGroup[0],
                        widgets: vizGroup[1],
                        totalWidgets: vizGroup[1].length,
                    });
                });
            }
        });
        return groupedWidgets;
    }

    private filterPrivate(widgets: WidgetSnapshot[]): WidgetSnapshot[] {
        return widgets.filter((widget) => !widget.isShared);
    }

    private filterGlobal(widgets: WidgetSnapshot[]): WidgetSnapshot[] {
        return widgets.filter((widget) => widget.isGlobal);
    }

    private formatResults(data: WidgetSnapshot[]): void {
        const widgets = this.getWidgetsByTab(data);
        const groupedWidgets = this.groupBy(widgets);

        groupedWidgets.forEach((widgetGroup, index) => {
            this.toggleWidgetData[index] = true;
            widgetGroup.widgets.forEach((widget) => {
                if (widget.coreWidgetType === 'VISUALIZATION') {
                    widget.vizForms = widget.visualizations.map((viz) => {
                        const vizConfig = visualizationDescriptorMap[viz];
                        return {
                            label: vizConfig.name,
                            cssClass: vizConfig.cssClass,
                            value: vizConfig.id,
                            uid: viz as VisualizationType,
                        };
                    });
                } else {
                    const widgetConfig = WidgetConfigurationManager.getWidgetByUID(widget.coreWidgetType)!;
                    widget.vizForms = [
                        {
                            label: widgetConfig.title,
                            cssClass: widgetConfig.cssClass!,
                            value: 'summaryWidget',
                            uid: widgetConfig.uid as VisualizationType,
                        },
                    ];
                }
            });

            if (this.groupText !== this.groupByWhatsNewText) {
                widgetGroup.widgets.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            } else {
                widgetGroup.widgets.sort((a, b) => {
                    return DdvDate.fromISOFormat(a.createdDate).isBefore(DdvDate.fromISOFormat(b.createdDate)) ? 1 : -1;
                });
            }
        });

        if (this.groupText !== this.groupByWhatsNewText) {
            groupedWidgets.sort((a, b) => a.groupName.toLowerCase().localeCompare(b.groupName.toLowerCase()));
        } else {
            groupedWidgets.sort((a, b) =>
                DdvDate.fromISOFormat(a.widgets[0].createdDate).isBefore(DdvDate.fromISOFormat(b.widgets[0].createdDate)) ? 1 : -1);
        }

        this.groupedWidgets = groupedWidgets;
    }

    private onWidgetDelete(widgetId: number, groupedWidgetsIndex: number, widgetIndexInGroup: number): void {
        this.featureFlagService.isFlagEnabled('use-aws-for-widgets', this.clientCode ?? '')
            .pipe(
                switchMap((isFlagEnabled) => {
                    if (isFlagEnabled) {
                        return this.widgetsService.deleteWidget(widgetId, this.clientCode);
                    }
                    return this.legacyWidgetsService.removeWidget(widgetId);
                }),
            )
            .subscribe({
                next: () => {
                    this.groupedWidgets[groupedWidgetsIndex].widgets.splice(widgetIndexInGroup, 1);
                    const widgetIndex = this.widgetsCollection.findIndex((widget) => widget.id === widgetId);
                    this.widgetsCollection.splice(widgetIndex, 1);
                },
                error: (
                    error: {
                        statusCode: number;
                        body?: {
                            // Delete this line after the migration is complete
                            clientFacingMessage?: string;
                            // Make this line required after the migration is complete
                            message?: string;
                            dashboards: { id: number, name: string, url?: string }[];
                        };
                    },
                ) => {
                    if (error.statusCode === 403 && error.body && Object.prototype.hasOwnProperty.call(error.body, 'dashboards')) {
                        const { clientFacingMessage, message, dashboards } = error.body;
                        const widgetDeleteAlert = this.modalService.open<void, WidgetDeleteAlertComponent>(
                            WidgetDeleteAlertComponent,
                            { windowClass: 'widget-delete-alert' });
                        widgetDeleteAlert.componentInstance.message = clientFacingMessage ?? message;
                        widgetDeleteAlert.componentInstance.dashboards = dashboards;
                    }
                },
            });
    }

    private addNamedQueryAttributesToWidgets(): void {
        this.widgetsCollection.forEach((widget: WidgetSnapshot) => {
            if (widget.namedQueryId) {
                const namedQuery = this.namedQueriesCollection.find((query) => query.id === widget.namedQueryId);
                widget.namedQueryName = namedQuery?.name;
                widget.namedQueryType = namedQuery?.type.name;
            }
        });
    }
}
